import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField
} from '@mui/material';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { APIService } from 'src/api/api';
import { setSuccessInfo, setErrorInfo } from 'src/redux/modalSlice';

function UpdateOdometerModal({
  open,
  onClose,
  transactionId,
  currentValue,
  vehicleNumber,
  additionalEntry,
  onSuccess
}) {
  const dispatch = useDispatch();

  const [odometer, setOdometer] = useState('');
  const [vehicle, setVehicle] = useState('');
  const [loading, setLoading] = useState(false);

  // show vehicle field only when vehicle_number is null and additional_entry exists
  const hasAdditionalEntryValue = String(additionalEntry ?? '').trim() !== '';
  const showVehicleField = vehicleNumber === null && hasAdditionalEntryValue;

  useEffect(() => {
    if (open) {
      setOdometer(currentValue ?? '');
      setVehicle(vehicleNumber ?? '');
    }
  }, [open, currentValue, vehicleNumber]);

  const handleChange = (event) => {
    const { value } = event.target;
    if (value === '' || (/^\d+$/.test(value) && Number(value) >= 0)) {
      setOdometer(value);
    }
  };

  const handleVehicleChange = (event) => {
    setVehicle(event.target.value.toUpperCase());
  };

  // helper to extract last 4 digits
  const getLastFourDigits = (value) => {
    if (!value) return '';
    const digits = value.toString().replace(/\D/g, '');
    return digits.slice(-4);
  };

  const handleSubmit = async () => {
    setLoading(true);

    try {
      // 🔹 new validation for last 4 digits
      if (showVehicleField) {
        const vehicleLast4 = getLastFourDigits(vehicle);
        const additionalLast4 = getLastFourDigits(additionalEntry);

        if (vehicleLast4 !== additionalLast4) {
          dispatch(
            setErrorInfo({
              errorMsg: 'Vehicle number last 4 digits must match additional entry last 4 digits'
            })
          );
          setLoading(false);
          return;
        }
      }

      const payload = {
        odometer_entry: Number(odometer)
      };

      if (showVehicleField) {
        payload.vehicle_number = vehicle.trim();
      }

      await APIService.updateOutwardOdometer(transactionId, payload);

      dispatch(
        setSuccessInfo({
          successMsg: 'Transaction updated successfully'
        })
      );

      onSuccess();
      onClose();
    } catch (error) {
      dispatch(
        setErrorInfo({
          errorMsg: 'Failed to update transaction'
        })
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Update Transaction</DialogTitle>

      <DialogContent>

        {showVehicleField && (
          <TextField
            label="Vehicle Number"
            value={vehicle}
            onChange={handleVehicleChange}
            fullWidth
            margin="normal"
          />
        )}

        <TextField
          label="Odometer Reading"
          type="number"
          value={odometer}
          onChange={handleChange}
          fullWidth
          margin="normal"
          inputProps={{ min: 0 }}
        />

      </DialogContent>

      <DialogActions>
        <Button
          onClick={onClose}
          variant="contained"
          color="primary"
          disabled={loading}
        >
          Cancel
        </Button>

        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={
            loading
            || odometer === ''
            || (showVehicleField && vehicle.trim() === '')
          }
        >
          Update
        </Button>
      </DialogActions>
    </Dialog>
  );
}

UpdateOdometerModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  transactionId: PropTypes.number.isRequired,
  currentValue: PropTypes.number,
  vehicleNumber: PropTypes.string,
  additionalEntry: PropTypes.string,
  onSuccess: PropTypes.func.isRequired
};

export default UpdateOdometerModal;
